/**
 * useUserData — local user state (visited, saved, collections, badges).
 *
 * Owns the per-device user profile and persists every mutation to
 * localStorage. For signed-in users the data is additionally synced to
 * Supabase (user_data_sync): on login the cloud row is merged with local
 * state (cloud wins additively), and every later mutation is debounce-
 * pushed back up. Anonymous users stay purely local.
 *
 * Consumers: App.tsx threads the returned data + setters into the
 * profile sheet, collection sheet, badge toasts and per-pin "visited"
 * toggles. App passes the auth user in so the cloud merge can run once
 * the lazy Supabase client + session actually exist.
 */
import { useState, useCallback, useEffect, useRef } from 'react';
import type { UserData, UserSiteVisit, UserList, RecentlyViewedItem, UserCollection, CollectionItem, LayerType, Country } from '../types/index.ts';
import { DEFAULT_USER_DATA } from '../constants/index.ts';
import { getISOWeek } from '../utils/format.ts';
import { supabase } from '../lib/supabase.ts';

function migrateCollections(data: UserData): UserData {
  if (data.collectionsMigrated) return data;
  const collections: UserCollection[] = data.collections ? [...data.collections] : [];
  const now = new Date().toISOString();
  if (data.saved?.length) {
    collections.push({
      id: crypto.randomUUID(), name: { da: "Gemte steder", no: "Lagrede steder", sv: "Sparade platser", en: "Saved places", fi: "Tallennetut", is: "Vista\u00f0ir sta\u00f0ir", kl: "Toqqorsimasut" }[localStorage.getItem("fornland-lang") || "da"] || "Saved places", emoji: "\u2764\ufe0f",
      items: data.saved.map(id => ({ siteId: id, layerType: "heritage" as LayerType, country: "DK" as Country, name: id, lng: 0, lat: 0, addedAt: now })),
      visibility: "private", created: now, updated: now,
    });
  }
  for (const list of data.lists ?? []) {
    collections.push({
      id: crypto.randomUUID(), name: list.name,
      items: list.items.map(id => ({ siteId: id, layerType: "heritage" as LayerType, country: "DK" as Country, name: id, lng: 0, lat: 0, addedAt: list.created })),
      visibility: "private", created: list.created, updated: list.created,
    });
  }
  return { ...data, collections, collectionsMigrated: true };
}

function loadUserData(): UserData {
  try {
    const raw = localStorage.getItem("fornland-user");
    if (!raw) return { ...DEFAULT_USER_DATA };
    const parsed = JSON.parse(raw);
    const merged = { ...DEFAULT_USER_DATA, ...parsed };
    return migrateCollections(merged);
  } catch { return { ...DEFAULT_USER_DATA }; }
}

function saveUserData(data: UserData) {
  try { localStorage.setItem("fornland-user", JSON.stringify(data)); } catch { /* storage full */ }
  syncToSupabase(data);
}

// Debounced sync to Supabase. The timeout is module-scoped on purpose: the
// hook is mounted once at the app root, and saveUserData is called from many
// callsites (some not inside the hook), so a hook-scoped ref wouldn't cover
// them. The timer is reset on every call; if the page unloads before it
// fires, the pending update is lost — but the data is already persisted to
// localStorage, so it'll resync on next mount.
let syncTimeout: ReturnType<typeof setTimeout> | null = null;
// Upserting before the cloud→local merge has run would overwrite the user's
// cloud history with whatever happens to be on this device (catastrophic on
// a fresh device: one visit wipes years of data). The flag flips once the
// merge effect in useUserData has reconciled cloud + local for the current
// user; until then mutations only persist locally and are pushed as part of
// the merged result.
 
let cloudMergeDone = false;
function syncToSupabase(data: UserData) {
  const sb = supabase;
  if (!sb || !cloudMergeDone) return;
  if (syncTimeout) clearTimeout(syncTimeout);
  syncTimeout = setTimeout(async () => {
    syncTimeout = null;
    try {
      const { data: { user } } = await sb.auth.getUser();
      if (!user) return;
      await sb.from("user_data_sync").upsert({
        user_id: user.id,
        data: data as unknown as Record<string, unknown>,
        updated_at: new Date().toISOString(),
      });
    } catch (e) { console.warn("[Fornland] Sync to Supabase failed:", e); }
  }, 2000);
}

// Merge: cloud wins for visits/badges (additive), local wins for ephemeral state
function mergeCloudLocal(cloud: UserData, local: UserData): UserData {
  return {
    ...cloud,
    // Merge visits (union of both)
    visited: { ...cloud.visited, ...local.visited },
    // Merge saved (union, deduplicated)
    saved: [...new Set([...cloud.saved, ...local.saved])],
    // Merge badges (union)
    badges: [...new Set([...cloud.badges, ...local.badges])],
    // Keep higher total
    totalVisits: Math.max(cloud.totalVisits, local.totalVisits),
    // Keep local ephemeral state
    recentlyViewed: local.recentlyViewed?.length ? local.recentlyViewed : cloud.recentlyViewed,
    lists: local.lists?.length ? local.lists : cloud.lists,
    // Keep earlier first visit date
    firstVisitDate: cloud.firstVisitDate && local.firstVisitDate
      ? (cloud.firstVisitDate < local.firstVisitDate ? cloud.firstVisitDate : local.firstVisitDate)
      : cloud.firstVisitDate || local.firstVisitDate,
    // Keep higher streak
    streakWeeks: Math.max(cloud.streakWeeks ?? 0, local.streakWeeks ?? 0),
    streakLastWeek: cloud.streakLastWeek || local.streakLastWeek,
  };
}

function useUserData(user?: { id: string } | null) {
  const [userData, setUserData] = useState<UserData>(loadUserData);
  const syncedForUser = useRef<string | null>(null);

  // When a signed-in user appears (auth resolves after the lazy Supabase
  // chunk loads), fetch their cloud row and merge it with local state.
  // Keyed on user.id so it runs once per account, not once per mount —
  // at mount time `supabase` is still null and a mount-only effect would
  // never see the session (the old version of this effect was dead code,
  // which meant local data could silently overwrite the cloud row).
  useEffect(() => {
    if (!user || !supabase) return;
    if (syncedForUser.current === user.id) return;
    cloudMergeDone = false; // re-arm the sync gate for this account
    const sb = supabase;
    (async () => {
      try {
        // Check if this is a DIFFERENT user than last time — if so, reset local data
        const lastUserId = localStorage.getItem("fornland-user-id");
        if (lastUserId && lastUserId !== user.id) {
          // Different user logged in — clear old user's local data
          const fresh = { ...DEFAULT_USER_DATA };
          setUserData(fresh);
          try { localStorage.setItem("fornland-user", JSON.stringify(fresh)); } catch { /* storage full */ }
        }
        try { localStorage.setItem("fornland-user-id", user.id); } catch { /* storage full */ }

        const { data: cloudRow } = await sb
          .from("user_data_sync")
          .select("data, updated_at")
          .eq("user_id", user.id)
          .maybeSingle();
        cloudMergeDone = true;
        syncedForUser.current = user.id;
        if (!cloudRow?.data) {
          // No cloud data yet — only push if this is the SAME user (not a fresh login on shared device)
          if (lastUserId === user.id) syncToSupabase(loadUserData());
          return;
        }
        const cloud = { ...DEFAULT_USER_DATA, ...(cloudRow.data as Record<string, unknown>) } as UserData;
        // Functional update so visits made between mount and the cloud
        // fetch resolving aren't dropped from the merge.
        setUserData(prev => {
          const merged = mergeCloudLocal(cloud, prev);
          saveUserData(merged);
          return merged;
        });
      } catch { /* silent */ }
    })();
  }, [user]);

  const persist = useCallback((next: UserData) => {
    setUserData(next);
    saveUserData(next);
    syncToSupabase(next);
  }, []);

  const markVisited = useCallback((siteId: string, visit: UserSiteVisit) => {
    setUserData(prev => {
      if (prev.visited[siteId]) return prev; // already visited
      const now = new Date();
      const currentWeek = getISOWeek(now);
      let streakWeeks = prev.streakWeeks;
      let streakLastWeek = prev.streakLastWeek;
      if (currentWeek !== streakLastWeek) {
        // Check if consecutive week
        const prevWeekDate = new Date(now.getTime() - 7 * 86400000);
        const prevWeek = getISOWeek(prevWeekDate);
        if (streakLastWeek === prevWeek) {
          streakWeeks = streakWeeks + 1;
        } else if (!streakLastWeek) {
          streakWeeks = 1;
        } else {
          streakWeeks = 1; // gap — reset
        }
        streakLastWeek = currentWeek;
      }
      const next: UserData = {
        ...prev,
        visited: { ...prev.visited, [siteId]: visit },
        totalVisits: prev.totalVisits + 1,
        firstVisitDate: prev.firstVisitDate || now.toISOString(),
        streakWeeks,
        streakLastWeek,
      };
      saveUserData(next);
      return next;
    });
  }, []);

  const unmarkVisited = useCallback((siteId: string) => {
    setUserData(prev => {
      if (!prev.visited[siteId]) return prev;
      const { [siteId]: _, ...rest } = prev.visited;
      const next: UserData = {
        ...prev,
        visited: rest,
        totalVisits: Math.max(0, prev.totalVisits - 1),
      };
      saveUserData(next);
      return next;
    });
  }, []);

  const toggleSaved = useCallback((siteId: string) => {
    setUserData(prev => {
      const idx = prev.saved.indexOf(siteId);
      const next: UserData = {
        ...prev,
        saved: idx >= 0 ? prev.saved.filter(id => id !== siteId) : [...prev.saved, siteId],
      };
      saveUserData(next);
      return next;
    });
  }, []);

  const isSaved = useCallback((siteId: string) => userData.saved.includes(siteId), [userData.saved]);
  const isVisited = useCallback((siteId: string) => siteId in userData.visited, [userData.visited]);

  const addBadge = useCallback((badgeId: string) => {
    setUserData(prev => {
      if (prev.badges.includes(badgeId)) return prev;
      const next: UserData = { ...prev, badges: [...prev.badges, badgeId] };
      saveUserData(next);
      return next;
    });
  }, []);

  const addNote = useCallback((siteId: string, note: string) => {
    setUserData(prev => {
      const visit = prev.visited[siteId];
      if (!visit) return prev;
      const next: UserData = {
        ...prev,
        visited: { ...prev.visited, [siteId]: { ...visit, notes: note } },
      };
      saveUserData(next);
      return next;
    });
  }, []);

  const addRecentlyViewed = useCallback((item: RecentlyViewedItem) => {
    setUserData(prev => {
      const existing = prev.recentlyViewed || [];
      const filtered = existing.filter(r => r.id !== item.id);
      const next: UserData = {
        ...prev,
        recentlyViewed: [item, ...filtered].slice(0, 12),
      };
      saveUserData(next);
      return next;
    });
  }, []);

  const createList = useCallback((name: string) => {
    setUserData(prev => {
      const lists = prev.lists || [];
      const newList: UserList = { id: `list-${Date.now()}`, name, items: [], created: new Date().toISOString() };
      const next: UserData = { ...prev, lists: [...lists, newList] };
      saveUserData(next);
      return next;
    });
  }, []);

  const addToList = useCallback((listId: string, siteId: string) => {
    setUserData(prev => {
      const lists = (prev.lists || []).map(l =>
        l.id === listId && !l.items.includes(siteId)
          ? { ...l, items: [...l.items, siteId] }
          : l
      );
      const next: UserData = { ...prev, lists };
      saveUserData(next);
      return next;
    });
  }, []);

  const removeFromList = useCallback((listId: string, siteId: string) => {
    setUserData(prev => {
      const lists = (prev.lists || []).map(l =>
        l.id === listId ? { ...l, items: l.items.filter(i => i !== siteId) } : l
      );
      const next: UserData = { ...prev, lists };
      saveUserData(next);
      return next;
    });
  }, []);

  const deleteList = useCallback((listId: string) => {
    setUserData(prev => {
      const next: UserData = { ...prev, lists: (prev.lists || []).filter(l => l.id !== listId) };
      saveUserData(next);
      return next;
    });
  }, []);

  const dismissTip = useCallback(() => {
    setUserData(prev => {
      const next: UserData = { ...prev, tipDismissedDate: new Date().toISOString().slice(0, 10) };
      saveUserData(next);
      return next;
    });
  }, []);

  const createCollection = useCallback((name: string, emoji?: string) => {
    setUserData(prev => {
      const now = new Date().toISOString();
      const newCollection: UserCollection = {
        id: crypto.randomUUID(), name, emoji,
        items: [], visibility: "private", created: now, updated: now,
      };
      const next: UserData = { ...prev, collections: [...(prev.collections || []), newCollection] };
      saveUserData(next);
      return next;
    });
  }, []);

  const addToCollection = useCallback((collectionId: string, item: CollectionItem) => {
    setUserData(prev => {
      const collections = (prev.collections || []).map(c =>
        c.id === collectionId && !c.items.some(i => i.siteId === item.siteId)
          ? { ...c, items: [...c.items, item], updated: new Date().toISOString() }
          : c
      );
      const next: UserData = { ...prev, collections };
      saveUserData(next);
      return next;
    });
  }, []);

  const removeFromCollection = useCallback((collectionId: string, siteId: string) => {
    setUserData(prev => {
      const collections = (prev.collections || []).map(c =>
        c.id === collectionId
          ? { ...c, items: c.items.filter(i => i.siteId !== siteId), updated: new Date().toISOString() }
          : c
      );
      const next: UserData = { ...prev, collections };
      saveUserData(next);
      return next;
    });
  }, []);

  const deleteCollection = useCallback((collectionId: string) => {
    setUserData(prev => {
      const next: UserData = { ...prev, collections: (prev.collections || []).filter(c => c.id !== collectionId) };
      saveUserData(next);
      return next;
    });
  }, []);

  const updateCollection = useCallback((collectionId: string, updates: Partial<Pick<UserCollection, "name" | "emoji" | "description" | "visibility">>) => {
    setUserData(prev => {
      const collections = (prev.collections || []).map(c =>
        c.id === collectionId ? { ...c, ...updates, updated: new Date().toISOString() } : c
      );
      const next: UserData = { ...prev, collections };
      saveUserData(next);
      return next;
    });
  }, []);

  return { userData, markVisited, unmarkVisited, toggleSaved, isSaved, isVisited, addBadge, addNote, persist, addRecentlyViewed, createList, addToList, removeFromList, deleteList, dismissTip, createCollection, addToCollection, removeFromCollection, deleteCollection, updateCollection };
}

export { loadUserData, saveUserData, useUserData };
